import { ProcessInfo } from "../../State/ProcessInfo";
import { EfficientStringSearchResult, getEfficientFunctionToSearchMultipleStringsCaseInsensitive } from "../EfficientMultiStringSearch";
import { Base64EncodingStep, hashAlgorithms, EncodingStep, HashEncodingStep, HexEncodingStep, LowercaseEncodingStep, UrlEncodingStep, UppercaseEncodingStep, EncodedSearchTerm, RemoveSpacesEncodingStep, DigitsOnlyEncodingStep, LettersOnlyEncodingStep, AsciiLettersOnlyEncodingStep, AlphanumericOnlyEncodingStep } from "./EncodedSearchTerm";
import { Md5 } from "ts-md5";

const md5 = (s: Uint8Array): ArrayBuffer => {
  const h = new Md5();
  h.appendByteArray(s);
  return (h.end(true) as Int32Array).buffer;
}

/*
*/
/**
 * Remove the terminal characters in a base64 string that may not match
 * within longer string that was base64 encoded.
 *
 * This is best understood through cases 2 and 3 of
 * https://datatracker.ietf.org/doc/html/rfc2045#section-6.8
 * > Special processing is performed if fewer than 24 bits are available
 *   at the end of the data being encoded.  A full encoding quantum is
 *   always completed at the end of a body.  When fewer than 24 input bits
 *   are available in an input group, zero bits are added (on the right)
 *   to form an integral number of 6-bit groups.  Padding at the end of
 *   the data is performed using the "=" character.  Since all base64
 *   input is an integral number of octets, only the following cases can
 *   arise: (1) the final quantum of encoding input is an integral
 *   multiple of 24 bits; here, the final unit of encoded output will be
 *   an integral multiple of 4 characters with no "=" padding, (2) the
 *   final quantum of encoding input is exactly 8 bits; here, the final
 *   unit of encoded output will be two characters followed by two "="
 *   padding characters, or (3) the final quantum of encoding input is
 *   exactly 16 bits; here, the final unit of encoded output will be three
 *   characters followed by one "=" padding character.
 *
 * For case (2), we remove the the final three characters:
 *   - the final data character containing two bits of data
 *	 - the two equal signs used to pad the string.
 * For case (3), we remove the final two characters:
 *   - the final data character containing four bits of data
 *   - the one equal sign used to pad the string.

 * @param base64 A base64 encoded string
 * @returns That string with 2-3 characters removed from the end so that we can
 * search for this string within a longer base-64 encoded string.
 */
const trimBase64PaddingPartialAndPadding = (base64: string): string => base64.endsWith('==') ? base64.slice(0, -3) :
  base64.endsWith('=') ? base64.slice(0, -2) :
    base64;

export const encodeBase64WithPartials = (...dataEncodings: EncodedSearchTerm[]): EncodedSearchTerm[] => dataEncodings.map(dataEncoding => {
  const { encodedSearchTerm, originalSearchTerm, encodingSteps } = dataEncoding;
  if (encodedSearchTerm.length < 5) return [];
  const bits = encodedSearchTerm.length * 8;
  const fullyEncoded: EncodedSearchTerm = {
    originalSearchTerm,
    encodingSteps: [...encodingSteps, { encodingType: 'base64', subtype: 'Full' } satisfies Base64EncodingStep],
    encodedSearchTerm: btoa(encodedSearchTerm)
  };
  // Remove any padding or characters that might be mixed with text added to end
  const alignedStart: EncodedSearchTerm = {
    originalSearchTerm,
    encodingSteps: [...encodingSteps, { encodingType: 'base64', subtype: 'AlignedStart' } satisfies Base64EncodingStep],
    encodedSearchTerm: trimBase64PaddingPartialAndPadding(btoa(encodedSearchTerm)).substring(0)
  };
  // Putting 2 characters before the string will put it two 6-bit base 64 characters and four bits past the start
  const removingFirstTwoBits: EncodedSearchTerm = {
    originalSearchTerm,
    encodingSteps: [...encodingSteps, { encodingType: 'base64', subtype: 'RemovingFirstTwoBits' } satisfies Base64EncodingStep],
    encodedSearchTerm: trimBase64PaddingPartialAndPadding(btoa('  ' + encodedSearchTerm)).substring(3)
  };
  // Putting 1 character before the string will put it one 6-bit base 64 character and two bits past the start
  const removingFirstFourBits: EncodedSearchTerm = {
    originalSearchTerm,
    encodingSteps: [...encodingSteps, { encodingType: 'base64', subtype: 'RemovingFirstFourBits' } satisfies Base64EncodingStep],
    encodedSearchTerm: trimBase64PaddingPartialAndPadding(btoa(' ' + encodedSearchTerm)).substring(2)
  };
  return [
    fullyEncoded,
    ...((alignedStart.encodedSearchTerm != fullyEncoded.encodedSearchTerm) ? [alignedStart] : []),
    removingFirstTwoBits,
    removingFirstFourBits
  ];
}).flat();

export const encodeHashes = async (...dataEncodings: EncodedSearchTerm[]): Promise<EncodedSearchTerm[]> => {
  return (await Promise.all(dataEncodings.map(async (dataEncoding) => {
    const { originalSearchTerm, encodedSearchTerm, encodingSteps } = dataEncoding;
    const msgUint8 = new TextEncoder().encode(encodedSearchTerm); // encode as (utf-8) Uint8Array
    return (await Promise.all(hashAlgorithms.map(async (algorithm) => {
      const hashBuffer = algorithm.toLocaleLowerCase() === "md5" ? md5(msgUint8) : await crypto.subtle.digest(algorithm, msgUint8); // hash the message
      const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
      const encodingStepsWithHash: EncodingStep[] = [...encodingSteps, { encodingType: 'hash', subtype: algorithm } as HashEncodingStep];
      const base64 = encodeBase64WithPartials({
        originalSearchTerm,
        encodedSearchTerm: String.fromCharCode(...hashArray),
        encodingSteps: encodingStepsWithHash
      });
      const hex: EncodedSearchTerm = {
        originalSearchTerm,
        encodedSearchTerm: hashArray.map(x => x.toString(16).padStart(2, '0')).join(''),
        encodingSteps: [...encodingStepsWithHash, { encodingType: 'hex' } as HexEncodingStep],
      };
      return [...base64, hex];
    }))).flat();
  }))).flat();
};
const encodeToLowercase = (...dataEncodings: EncodedSearchTerm[]): EncodedSearchTerm[] => {
  const results: EncodedSearchTerm[] = [];
  for (const dataEncoding of dataEncodings) {
    const { originalSearchTerm, encodedSearchTerm, encodingSteps } = dataEncoding;
    const lc = encodedSearchTerm.toLocaleLowerCase();
    // Only add the lowercase encoding if it's different from the original
    if (lc != originalSearchTerm) {
      results.push({
        originalSearchTerm,
        encodedSearchTerm: lc,
        encodingSteps: [...encodingSteps, { encodingType: 'lowercase' } as LowercaseEncodingStep]
      });
    }
  }
  return results;
};
const encodeToUppercase = (...dataEncodings: EncodedSearchTerm[]): EncodedSearchTerm[] => {
  const results: EncodedSearchTerm[] = [];
  for (const dataEncoding of dataEncodings) {
    const { originalSearchTerm, encodedSearchTerm, encodingSteps } = dataEncoding;
    const lc = encodedSearchTerm.toLocaleUpperCase();
    // Only add the uppercase encoding if it's different from the original
    if (lc != originalSearchTerm) {
      results.push({
        originalSearchTerm,
        encodedSearchTerm: lc,
        encodingSteps: [...encodingSteps, { encodingType: 'uppercase' } as UppercaseEncodingStep]
      });
    }
  }
  return results;
};
const urlEncode = (...dataEncodings: EncodedSearchTerm[]): EncodedSearchTerm[] => {
  const results: EncodedSearchTerm[] = [];
  for (const dataEncoding of dataEncodings) {
    const { originalSearchTerm, encodedSearchTerm, encodingSteps } = dataEncoding;
    const urlEncoded = encodeURIComponent(encodedSearchTerm);
    // Only add the url encoding if it's different from the original
    if (urlEncoded != originalSearchTerm) {
      results.push({
        originalSearchTerm,
        encodedSearchTerm: urlEncoded,
        encodingSteps: [...encodingSteps, { encodingType: 'urlEncode' } as UrlEncodingStep]
      });
    }
  }
  return results;
};

const removeWhiteSpace = (...dataEncodings: EncodedSearchTerm[]): EncodedSearchTerm[] => {
  const results: EncodedSearchTerm[] = [];
  for (const dataEncoding of dataEncodings) {
    const { originalSearchTerm, encodedSearchTerm, encodingSteps } = dataEncoding;
    const whiteSpaceRemoved = encodedSearchTerm.replace(/\s/g,'');
    // Only add the url encoding if it's different from the original
    if (whiteSpaceRemoved != originalSearchTerm && whiteSpaceRemoved.length > 3) {
      results.push({
        originalSearchTerm,
        encodedSearchTerm: whiteSpaceRemoved,
        encodingSteps: [...encodingSteps, { encodingType: 'removeSpaces' } as RemoveSpacesEncodingStep]
      });
    }
  }
  return results;
}

const digitsOnly = (...dataEncodings: EncodedSearchTerm[]): EncodedSearchTerm[] => {
  const results: EncodedSearchTerm[] = [];
  for (const dataEncoding of dataEncodings) {
    const { originalSearchTerm, encodedSearchTerm, encodingSteps } = dataEncoding;
    const digitsOnly = encodedSearchTerm.replace(/[^\d]/g,'');
    // Only add the url encoding if it's different from the original
    if (digitsOnly != originalSearchTerm && digitsOnly.length > 4 && digitsOnly.length >= Math.floor((originalSearchTerm.length/2))) {
      results.push({
        originalSearchTerm,
        encodedSearchTerm: digitsOnly,
        encodingSteps: [...encodingSteps, { encodingType: 'digitsOnly' } as DigitsOnlyEncodingStep]
      });
    }
  }
  return results;
}

export const keepOnlyLetters = (s: string) => s.replace(/[^\p{L}]/ug,'');

export const keepOnlyAlphanumeric = (s: string) => s.replace(/[^\p{L}\p{Nd}]/ug,'');

const lettersOnly = (...dataEncodings: EncodedSearchTerm[]): EncodedSearchTerm[] => {
  const results: EncodedSearchTerm[] = [];
  for (const dataEncoding of dataEncodings) {
    const { originalSearchTerm, encodedSearchTerm, encodingSteps } = dataEncoding;
    const lettersOnly = keepOnlyLetters(encodedSearchTerm);
    // Only add the url encoding if it's different from the original
    if (lettersOnly != originalSearchTerm && lettersOnly.length > 3 && lettersOnly.length >= Math.floor((originalSearchTerm.length/2))) {
      results.push({
        originalSearchTerm,
        encodedSearchTerm: lettersOnly,
        encodingSteps: [...encodingSteps, { encodingType: 'lettersOnly' } as LettersOnlyEncodingStep]
      });
    }
  }
  return results;
}

const alphanumericOnly = (...dataEncodings: EncodedSearchTerm[]): EncodedSearchTerm[] => {
  const results: EncodedSearchTerm[] = [];
  for (const dataEncoding of dataEncodings) {
    const { originalSearchTerm, encodedSearchTerm, encodingSteps } = dataEncoding;
    const alphanumericOnly = keepOnlyAlphanumeric(encodedSearchTerm);
    // Only add the url encoding if it's different from the original
    if (alphanumericOnly != originalSearchTerm && alphanumericOnly.length > 3 && alphanumericOnly.length >= Math.floor((originalSearchTerm.length/2))) {
      results.push({
        originalSearchTerm,
        encodedSearchTerm: alphanumericOnly,
        encodingSteps: [...encodingSteps, { encodingType: 'alphanumericOnly' } as AlphanumericOnlyEncodingStep]
      });
    }
  }
  return results;
}

const asciiLettersOnly = (...dataEncodings: EncodedSearchTerm[]): EncodedSearchTerm[] => {
  const results: EncodedSearchTerm[] = [];
  for (const dataEncoding of dataEncodings) {
    const { originalSearchTerm, encodedSearchTerm, encodingSteps } = dataEncoding;
    const asciiLettersOnly = encodedSearchTerm.replace(/[^a-zA-Z]/g,'');
    // Only add the url encoding if it's different from the original
    if (asciiLettersOnly != originalSearchTerm && asciiLettersOnly.length > 3 && asciiLettersOnly.length >= Math.floor((originalSearchTerm.length/2))) {
      results.push({
        originalSearchTerm,
        encodedSearchTerm: asciiLettersOnly,
        encodingSteps: [...encodingSteps, { encodingType: 'asciiLettersOnly' } as AsciiLettersOnlyEncodingStep]
      });
    }
  }
  return results;
}

export const getEncodingsToSearchFor = async (...values: string[]) => {
  let startMs = performance.now();
  const encodings = new Map<string, EncodedSearchTerm>();
  const addEncodings = (...encodingsToAdd: EncodedSearchTerm[]) => {
    for (const encoding of encodingsToAdd) {
      if (!encodings.has(encoding.encodedSearchTerm)) {
        encodings.set(encoding.encodedSearchTerm, encoding);
      }
    }
  }
  ProcessInfo.log(`Creating encodings for `, values);
  const unencoded: EncodedSearchTerm[] = values
    .map( value => value.replaceAll(/[^\x20-\x7E]/g, '') ) // Remove non-ascii characters)
    .map( value => ({
    originalSearchTerm: value,
    encodedSearchTerm: value,
    encodingSteps: []
  }));;
  addEncodings(...unencoded);
  ProcessInfo.log(`${encodings.size} unencoded`);
  // Add copies of encodings with spaces removed
  addEncodings(...removeWhiteSpace(...encodings.values()));
  ProcessInfo.log(`${encodings.size} after removing whitespace`);
  // Add digits/letters only
  addEncodings(...digitsOnly(...encodings.values()))
  ProcessInfo.log(`${encodings.size} after digits only`);
  addEncodings(...lettersOnly(...encodings.values()));
  ProcessInfo.log(`${encodings.size} after letters only`);
  addEncodings(...alphanumericOnly(...encodings.values()));
  ProcessInfo.log(`${encodings.size} after alphanumeric only`);
  addEncodings(...asciiLettersOnly(...encodings.values()));
  ProcessInfo.log(`${encodings.size} after ascii letters only`);
  // Add urlEncoded copies of all existing encodings
  addEncodings(...urlEncode(...encodings.values()));
  ProcessInfo.log(`${encodings.size} after single url encoding`);
  // Lots of sites (accidentally?) urlEncode their already-urlEncoded data, so we'll do it twice too.
  addEncodings(...urlEncode(...encodings.values()));
  ProcessInfo.log(`${encodings.size} after double encoding`);
  const caseInsensitiveEncodings = [...encodeToLowercase(...encodings.values()), ...encodeToUppercase(...encodings.values())]
  ProcessInfo.log(`${encodings.size} after upper/lowercase`);
  // Add base64 encodings of all above encodings
  addEncodings(...encodeBase64WithPartials(...encodings.values(), ...caseInsensitiveEncodings));
  ProcessInfo.log(`${encodings.size} base64 encodings (including partials)`);
  // Add hashes of all current encodings
  addEncodings(...(await encodeHashes(...encodings.values(), ...caseInsensitiveEncodings)));
  ProcessInfo.log(`${encodings.size} after hashing`);
  ProcessInfo.log(`Time to encode ${values.length} terms into ${encodings.size} encodings: ${performance.now() - startMs}ms`);
  return encodings;
};

export interface EfficientEncodedStringSearchResult extends EncodedSearchTerm, EfficientStringSearchResult { }
export type EfficientEncodedStringSearchFunction = (contentToSearch: string) => EfficientEncodedStringSearchResult[];
export const getSearchFunctionForPotentiallyEncodedStrings = async (searchTerms: string[]) => {
  const encodings = await getEncodingsToSearchFor(...searchTerms);
  // ProcessInfo.log("Updated encodings", [...encodings.entries()]);
  // for (const encoding of searchTermEncodings) {
  //   const existing = encodedSearchStringToEncoding.get(encoding.encodedSearchTerm);
  //   if (existing == null || existing.encodingSteps.length > encoding.encodingSteps.length) {
  //     encodedSearchStringToEncoding.set(encoding.encodedSearchTerm, encoding);
  //   }
  // }
  const searchFunction = getEfficientFunctionToSearchMultipleStringsCaseInsensitive([...encodings.keys()], );
  const searchFunctionForPotentiallyEncodedStrings: EfficientEncodedStringSearchFunction = (...args: Parameters<typeof searchFunction>): EfficientEncodedStringSearchResult[] =>
    searchFunction(...args)
      .map((result) => {
        const encodedSearchTerm = encodings.get(result.matchedString);
        if (encodedSearchTerm == null) return;
        return {
          ...result,
          ...encodedSearchTerm,
        } satisfies EfficientEncodedStringSearchResult;
      }).filter( x => x != null);
  return searchFunctionForPotentiallyEncodedStrings;
}
